import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { CSSProperties, ForwardedRef, ReactElement, forwardRef } from 'react';
import { Locale } from '@synergy/models';
import { getCurrencySymbol } from '@synergy/utils';
import { styled } from '@mui/material';
import { useIntl } from 'react-intl';

import { PoolTextfield, PoolTextfieldProps } from '../pool-textfield';

interface EventStub {
    target: {
        id: string;
        name: string;
        value?: number;
    };
}

interface NumericTextfieldProps {
    id: string;
    name: string;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    onChange: (event: EventStub) => void;
    onBlur: (event: EventStub) => void;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    hasThousandSeparator?: boolean;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
}

function NumericTextfieldRef(
    props: NumericTextfieldProps,
    ref: ForwardedRef<NumberFormat<NumericTextfieldProps>>
): ReactElement {
    const {
        id,
        name,
        onChange,
        onBlur,
        fixedDecimalScale,
        decimalScale,
        noFractionalNumbers,
        noNegativeNumbers,
        hasThousandSeparator = true,
        placeholder,
        ...other
    } = props;

    const intl = useIntl();
    const decimalNumber = 1000.1;
    const parts = intl.formatNumberToParts(decimalNumber);
    const thousandSeparator = hasThousandSeparator ? parts.find(part => part.type === 'group')?.value ?? '.' : false;
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? ',';

    const handleValueChange = (values: NumberFormatValues): void => {
        const value = values.floatValue;
        onChange({ target: { id, name, value } });
    };

    const handleBlur = (): void => {
        onBlur({ target: { id, name } });
    };

    return (
        <NumberFormat
            {...other}
            name={name}
            id={id}
            getInputRef={ref}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={noFractionalNumbers ? 0 : decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            allowNegative={!noNegativeNumbers}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            placeholder={placeholder || '0'}
        />
    );
}

const NumericTextfield = forwardRef(NumericTextfieldRef);

export type PoolNumberTextfieldProps = PoolTextfieldProps & {
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    hasThousandSeparator?: boolean;
    prefix?: string;
    suffix?: string;
    currency?: string;
    /**
     * Whether the currency sign stands in the field.
     *
     * In a table it does not: the sign belongs in the column heading, and
     * repeated down fifty rows it only costs the figures room (LST-S17).
     * The figure keeps its formatting either way.
     */
    currencySymbol?: boolean;
    locale?: Locale;
};

// HINT: If used with Formik, the value needs to be "null" for proper validation
export const PoolNumberTextfield = (props: PoolNumberTextfieldProps): ReactElement => {
    const {
        noFractionalNumbers,
        noNegativeNumbers,
        fixedDecimalScale,
        decimalScale,
        suffix,
        prefix,
        hasThousandSeparator,
        currency,
        currencySymbol = true,
        locale,
        ...textfieldProps
    } = props;

    const inputProps = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputComponent: NumericTextfield as any,
        inputProps: {
            noFractionalNumbers,
            noNegativeNumbers,
            fixedDecimalScale,
            decimalScale,
            hasThousandSeparator,
            suffix: !suffix && currencySymbol && currency && locale ? ` ${getCurrencySymbol(currency, locale)}` : suffix,
            prefix,
            currency,
            currencySymbol,
            locale,
            name: props.name,
            ...textfieldProps.inputProps,
        },
    };

    // The caller's InputProps are merged rather than replaced, and the numeric
    // input wins: passing an adornment used to drop inputComponent and with it
    // the whole number formatting (#2215, R-69).
    return <PoolTextfield {...textfieldProps} InputProps={{ ...textfieldProps.InputProps, ...inputProps }} />;
};
